import * as Api from '@ViewModels';
import { useInfiniteQuery, useMutation } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';

export const useFilterGoogleBusinessReviews = ({
	onError,
	onSuccess,
}: {
	onError?: (err: Api.IOperationResultNoValue) => void;
	onSuccess?: (value: Api.IPagedCollection<Api.IGoogleBusinessReview>) => void;
}) => {
	const userSession = useUserSession();

	return useMutation({
		mutationFn: (filter: Api.IGoogleBusinessReviewFilterRequest) => {
			return userSession.webServiceHelper.callAsync<Api.IPagedCollection<Api.IGoogleBusinessReview>>(
				'googleBusiness/reviews/filter',
				'POST',
				filter
			);
		},
		onError,
		onSuccess,
	});
};

export const useInfiniteFilterGoogleBusinessReviews = ({
	enabled = true,
	impersonationContext,
	pageSize = 20,
	refetchOnWindowFocus = false,
	filterRequest,
	sort,
	sortBy,
}: {
	enabled?: boolean;
	filterRequest?: Api.IGoogleBusinessReviewFilterRequest;
	impersonationContext?: Api.IImpersonationContext;
	pageSize?: number;
	refetchOnWindowFocus?: boolean;
	sort?: 'asc' | 'desc';
	sortBy?: 'ratingStringSort' | 'createTime';
}) => {
	const userSession = useUserSession();

	return useInfiniteQuery<Api.IPagedCollection<Api.IGoogleBusinessReview>>({
		enabled: enabled && !!filterRequest,
		getNextPageParam: lastPage => lastPage.pageToken,
		queryFn: ({ pageParam }) => {
			return userSession.webServiceHelper.callAsync<Api.IPagedCollection<Api.IGoogleBusinessReview>>(
				Api.ImpersonationBroker.composeApiUrl({
					impersonationContext,
					queryParams: { pageSize, pageToken: pageParam, sort, sortBy },
					urlPath: 'googleBusiness/reviews/filter',
				}),
				'POST',
				filterRequest
			);
		},
		queryKey: ['googleBusinessReviewsFilter', filterRequest, pageSize],
		refetchOnWindowFocus,
		initialPageParam: '',
	});
};
